import React from 'react'
import styled from 'styled-components'
import { SchoolHelper } from '@laputan/helpers'
import { GeoLocationHelper } from '@laputan/helpers'
import { Link } from '../../../components/Link'
import { SchoolLogo } from '../../SchoolLogo'
import { Text } from '../../../components/Text'
import { SCREEN_SIZE, STYLES } from '../../../static/style'
import { SchoolFeeBadge } from '../../Badge/SchoolFeeBadge'
import { SectorBadge } from '../../Badge/SectorBadge'
import { LevelBadge } from '../../Badge/LevelBadge'
import { GenderBadge } from '../../Badge/GenderBadge'
import { SchoolResultBadgeGroup } from '../../Badge/SchoolResultBadgeGroup'
import { CONFIG } from '../../../config'
import { pathOr } from '../../../lib/ramda'
import { School } from '../../props/School.types'
import { LatLng } from '../../../types/LatLng.types'

const LeftContentSection = styled.div`
  float: right;
  text-align: right;
  margin-top: -0.5rem;
  margin-right: -0.5rem;
`

const StyledSchoolLogo = styled(SchoolLogo)`
  padding: 0.5rem;
  margin-right: 1rem;
  margin-top: 0.5rem;
  ${({ logo }) => !logo && 'visibility: hidden;'}
  ${SCREEN_SIZE.MD} {
    margin-top: 0.75rem;
    visibility: visible;
  }
`

const StyledSchoolListRow = styled(Link)`
  position: relative;
  overflow: hidden;
  ${({ $isHorizontal }: { $isHorizontal: boolean }) =>
    $isHorizontal &&
    `
    width: 18.2rem;
  `}
  margin: .5rem;
  border-radius: ${STYLES.BORDER_RADIUS};
  background-color: ${STYLES.COLOR_WHITE};
  color: currentColor;
  border-bottom: 1px solid ${STYLES.BORDER_COLOR};
  cursor: pointer;
  padding: 0.5rem 0.5rem;
  border: 1px solid ${STYLES.BORDER_COLOR};
  ${({ featured }) =>
    featured === 'true' &&
    `
    border-color: ${STYLES.PRIMARY_COLOR};
  `}
  ${({ alignlogotoright }) =>
    alignlogotoright === 'true'
      ? `
    display: block;
  `
      : `
    display: block;
    ${SCREEN_SIZE.MD} {
      display: flex;
    }
  `}
  &:hover {
    color: currentColor;
    background-color: ${STYLES.COLOR_AQUA_HAZE_BLUE};
  }
`

const SchoolNameText = styled(Text)`
  font-weight: 600;
  margin-right: 1.5rem;
`

const DistanceText = styled(Text)`
  font-size: ${STYLES.FONT_SIZE_SM};
`

const LocationText = styled(Text)`
  color: ${STYLES.COLOR_PALE_SKY_GRAY};
  font-size: ${STYLES.FONT_SIZE_SM};
  display: block;
  margin: ${STYLES.GUTTER} ${STYLES.GUTTER} ${STYLES.GUTTER} 0;
  ${SCREEN_SIZE.MD} {
    display: inline-block;
  }
`

const ContentSection = styled.div`
  padding: 0.25rem 0.5rem;
`

const SubContentSection = styled.div``

const StyledSectorBadge = styled(SectorBadge)`
  margin-left: 0;
`

const StyledLevelBadge = styled(LevelBadge)`
  margin-left: 0;
`

const StyledGenderBadge = styled(GenderBadge)`
  margin-left: 0;
`

interface ISchoolListRowProps {
  school: School
  coords?: LatLng
  isOpenInNewTab?: boolean
  isAlignLogoToRight?: boolean
  isHorizontal?: boolean
  isShowLevel?: boolean
  isShowSector?: boolean
  isShowGender?: boolean
  isShowSchoolResults?: boolean
  isShowSchoolFees?: boolean
  isShowDistance?: boolean
  className?: string
}

const SchoolListRow: React.FC<ISchoolListRowProps> = ({
  school,
  coords = {},
  className,
  isOpenInNewTab = false,
  isAlignLogoToRight = false,
  isHorizontal,
  isShowLevel = true,
  isShowSector = true,
  isShowGender = true,
  isShowSchoolResults = true,
  isShowSchoolFees = true,
  isShowDistance = true,
}) => {
  const {
    logo,
    name,
    sector,
    level,
    fee,
    gender,
    location,
    results,
    isFeatured,
    mathsRating,
    englishRating,
  } = school
  const schoolUrl = SchoolHelper.normalizeSchoolUrlHandle(school)
  let distance
  const [schoolLng, schoolLat] = pathOr(
    [undefined, undefined],
    ['location', 'coords'],
    school
  )

  if (
    isShowDistance &&
    !!schoolLng &&
    !!schoolLat &&
    !!coords.lat &&
    !!coords.lng
  ) {
    distance = GeoLocationHelper.getDistanceFromLatLonInKm({
      from: { lat: schoolLat, lng: schoolLng },
      to: { lat: coords.lat, lng: coords.lng },
    })
  }
  return (
    <StyledSchoolListRow
      className={className}
      to={isOpenInNewTab ? `${CONFIG.WEBSITE_SITEURL}${schoolUrl}` : schoolUrl}
      featured={`${isFeatured}`}
      alignlogotoright={isAlignLogoToRight ? 'true' : 'false'}
      $isHorizontal={isHorizontal}
    >
      <LeftContentSection>
        <StyledSchoolLogo alt={name} logo={logo} />
      </LeftContentSection>
      <ContentSection>
        <SubContentSection>
          <SchoolNameText>{name}</SchoolNameText>
          {location && (
            <LocationText>
              {`${location.suburb}, ${location.state}`}
              {distance && <DistanceText>{` - ${distance}km`}</DistanceText>}
            </LocationText>
          )}
        </SubContentSection>
        <SubContentSection>
          {isShowLevel && level && <StyledLevelBadge>{level}</StyledLevelBadge>}
          <br />
          {isShowSector && sector && (
            <StyledSectorBadge>{sector}</StyledSectorBadge>
          )}
          {isShowGender && gender && (
            <StyledGenderBadge>{gender}</StyledGenderBadge>
          )}
          {isShowSchoolFees && fee && (
            <SchoolFeeBadge title="Local" fee={fee.local} />
          )}
          {isShowSchoolFees && fee && (
            <SchoolFeeBadge title="International" fee={fee.international} />
          )}
        </SubContentSection>
        {isShowSchoolResults && (
          <SubContentSection>
            <SchoolResultBadgeGroup
              results={results}
              mathsRating={mathsRating}
              englishRating={englishRating}
            />
          </SubContentSection>
        )}
      </ContentSection>
    </StyledSchoolListRow>
  )
}

export { SchoolListRow }
